import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import type { Garden, OutboxOp, Photo, Plant } from "./types";

interface Schema extends DBSchema {
  gardens: { key: string; value: Garden };
  plants: { key: string; value: Plant; indexes: { garden: string } };
  photos: { key: string; value: Photo; indexes: { plant: string; garden: string } };
  outbox: { key: string; value: OutboxOp };
  meta: { key: string; value: { k: string; v: any } };
}

let dbp: Promise<IDBPDatabase<Schema>> | null = null;
export function db() {
  if (!dbp) dbp = openDB<Schema>("zahrada", 2, {
    upgrade(d, oldVersion, _newVersion, tx) {
      if (oldVersion < 1) {
        d.createObjectStore("gardens", { keyPath: "id" });
        d.createObjectStore("plants", { keyPath: "id" }).createIndex("garden", "garden_id");
        const p = d.createObjectStore("photos", { keyPath: "id" });
        p.createIndex("plant", "plant_id");
        p.createIndex("garden", "garden_id");
        d.createObjectStore("outbox", { keyPath: "key" });
        d.createObjectStore("meta", { keyPath: "k" });
      } else if (oldVersion === 1) {
        if ((d.objectStoreNames as any).contains("trees")) (d as any).deleteObjectStore("trees");
        if (!d.objectStoreNames.contains("plants")) {
          d.createObjectStore("plants", { keyPath: "id" }).createIndex("garden", "garden_id");
        }
        if (d.objectStoreNames.contains("photos")) {
          const p = tx.objectStore("photos") as any;
          if (p.indexNames.contains("tree")) p.deleteIndex("tree");
          if (!p.indexNames.contains("plant")) p.createIndex("plant", "plant_id");
        }
        if (d.objectStoreNames.contains("meta")) {
          tx.objectStore("meta").clear();
        }
      }
    },
  });
  return dbp;
}

// In-memory store for the demo tour: nothing touches IndexedDB, so nothing survives a reload.
let mem: { gardens: Map<string, Garden>; plants: Map<string, Plant>; photos: Map<string, Photo>; meta: Map<string, any> } | null = null;
export function useMemory() { mem = { gardens: new Map(), plants: new Map(), photos: new Map(), meta: new Map() }; }

export async function getMeta<T>(k: string, fallback: T): Promise<T> {
  if (mem) return mem.meta.has(k) ? mem.meta.get(k) : fallback;
  const r = await (await db()).get("meta", k);
  return r ? (r.v as T) : fallback;
}
export async function setMeta(k: string, v: unknown) {
  if (mem) { mem.meta.set(k, v); return; }
  await (await db()).put("meta", { k, v });
}

export async function gardens() { return mem ? [...mem.gardens.values()] : (await db()).getAll("gardens"); }
export async function putGardens(list: Garden[]) {
  if (mem) {
    const next = new Map<string, Garden>();
    for (const g of list) {
      const prev = mem.gardens.get(g.id);
      next.set(g.id, prev?.photo_blob && !g.photo_blob ? { ...g, photo_blob: prev.photo_blob } : g);
    }
    mem.gardens = next;
    return;
  }
  const d = await db();
  const prevAll = await d.getAll("gardens");
  const prevMap = new Map(prevAll.map((g) => [g.id, g]));
  const tx = d.transaction("gardens", "readwrite");
  await tx.store.clear();
  for (const g of list) {
    const prev = prevMap.get(g.id);
    const keepBlob = prev?.photo_blob && (!g.updated_at || prev.updated_at === g.updated_at);
    await tx.store.put(keepBlob && !g.photo_blob ? { ...g, photo_blob: prev.photo_blob } : g);
  }
  await tx.done;
}
export async function putGarden(g: Garden) {
  if (mem) { mem.gardens.set(g.id, g); return; }
  await (await db()).put("gardens", g);
}
export async function deleteGarden(id: string) {
  if (mem) {
    mem.gardens.delete(id);
    for (const [k, t] of mem.plants) if (t.garden_id === id) mem.plants.delete(k);
    for (const [k, p] of mem.photos) if (p.garden_id === id) mem.photos.delete(k);
    return;
  }
  const d = await db();
  await d.delete("gardens", id);
  const pls = await d.getAllFromIndex("plants", "garden", id);
  for (const t of pls) await d.delete("plants", t.id);
  const phs = await d.getAllFromIndex("photos", "garden", id);
  for (const p of phs) await d.delete("photos", p.id);
  const ops = await d.getAll("outbox");
  for (const o of ops) if (o.garden_id === id) await d.delete("outbox", o.key);
}

export async function plantsOf(gardenId: string) {
  const all = mem ? [...mem.plants.values()].filter((t) => t.garden_id === gardenId) : await (await db()).getAllFromIndex("plants", "garden", gardenId);
  return all.filter((t) => !t.deleted_at);
}
export async function getPlant(id: string) { return mem ? mem.plants.get(id) : (await db()).get("plants", id); }
export async function putPlant(t: Plant) {
  if (mem) { mem.plants.set(t.id, t); return; }
  await (await db()).put("plants", t);
}

export async function photosOfPlant(plantId: string) {
  const all = mem ? [...mem.photos.values()].filter((p) => p.plant_id === plantId) : await (await db()).getAllFromIndex("photos", "plant", plantId);
  return all.filter((p) => !p.deleted_at).sort((a, b) => a.created_at.localeCompare(b.created_at));
}
export async function photosOfGarden(gardenId: string) {
  const all = mem ? [...mem.photos.values()].filter((p) => p.garden_id === gardenId) : await (await db()).getAllFromIndex("photos", "garden", gardenId);
  return all.filter((p) => !p.deleted_at);
}
export async function getPhoto(id: string) { return mem ? mem.photos.get(id) : (await db()).get("photos", id); }
export async function putPhoto(p: Photo) {
  if (mem) { mem.photos.set(p.id, p); return; }
  await (await db()).put("photos", p);
}

// The demo never syncs, so its queue stays empty.
export async function outbox() { return mem ? [] : (await (await db()).getAll("outbox")).sort((a, b) => a.at - b.at); }
export async function queue(op: Omit<OutboxOp, "key" | "at">) {
  if (mem) return;
  // one pending op per object+type; a newer edit replaces the older one
  const key = op.type + ":" + op.id;
  await (await db()).put("outbox", { ...op, key, at: Date.now() } as OutboxOp);
}
export async function unqueue(key: string) { if (!mem) await (await db()).delete("outbox", key); }
export async function pendingCount() { return mem ? 0 : (await db()).count("outbox"); }

export async function wipeAll() {
  if (mem) { useMemory(); return; }
  const d = await db();
  for (const s of ["gardens", "plants", "photos", "outbox", "meta"] as const) await d.clear(s);
}
